/* ==========================================================================
   TYMZO GLOBAL MOTION V14
   Animations globales premium, légères et accessibles
   ========================================================================== */

(() => {
  'use strict';

  const reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  const REVEAL_SELECTOR = '[data-tymzo-reveal]';

  const AUTO_REVEAL_SELECTORS = [
  '.product-card--tymzo',
  '.tymzo-product-benefits',
  '.tymzo-product-bundle',
  '.tymzo-product-upsell',
  '.tymzo-product-faq',
  '.tymzo-product-reassurance',
  '.tymzo-cart-premium'
].join(',');

  const STAGGER_CONTAINERS = [
    '.product-grid',
    '.collection-list',
    '.tymzo-product-bundle__products',
    '.tymzo-product-reassurance',
    '.tymzo-cart-premium__reassurance'
  ].join(',');

  const injectStyles = () => {
    if (document.getElementById('TymzoGlobalMotionStyles')) return;

    const style = document.createElement('style');
    style.id = 'TymzoGlobalMotionStyles';

    style.textContent = `
      [data-tymzo-reveal] {
        opacity: 0;
        transform: translateY(24px);
        transition:
          opacity .65s cubic-bezier(.22, 1, .36, 1),
          transform .65s cubic-bezier(.22, 1, .36, 1);
        transition-delay: var(--tymzo-reveal-delay, 0ms);
        will-change: opacity, transform;
      }

      [data-tymzo-reveal="soft"] {
        transform: translateY(14px);
      }

      [data-tymzo-reveal="scale"] {
        transform: translateY(18px) scale(.975);
      }

      [data-tymzo-reveal].is-visible {
        opacity: 1;
        transform: translateY(0) scale(1);
        will-change: auto;
      }

      .tymzo-motion-button {
        transition:
          transform .22s ease,
          box-shadow .22s ease,
          background-color .22s ease;
      }

      .tymzo-motion-image {
        overflow: hidden;
      }

      .tymzo-motion-image img {
        transition: transform .55s cubic-bezier(.22, 1, .36, 1);
      }

      @media (hover: hover) {
        .tymzo-motion-button:hover {
          transform: translateY(-2px);
        }

        .tymzo-motion-image:hover img {
          transform: scale(1.035);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        [data-tymzo-reveal] {
          opacity: 1 !important;
          transform: none !important;
          transition: none !important;
        }

        .tymzo-motion-button,
        .tymzo-motion-image img {
          transition: none !important;
        }
      }
    `;

    document.head.appendChild(style);
  };

  const markElements = (root = document) => {
    root.querySelectorAll(AUTO_REVEAL_SELECTORS).forEach((element) => {
      if (
        element.matches('header, footer, .shopify-section-group-header-group') ||
        element.closest('dialog[open]')
      ) {
        return;
      }

      if (!element.hasAttribute('data-tymzo-reveal')) {
        element.setAttribute('data-tymzo-reveal', 'soft');
      }
    });

    root.querySelectorAll(
      'button, .button, [class*="__button"], [class*="__cta"]'
    ).forEach((button) => {
      button.classList.add('tymzo-motion-button');
    });

    root.querySelectorAll(
      '.product-card__media, .product-media-container, .tymzo-product-bundle__media'
    ).forEach((media) => {
      media.classList.add('tymzo-motion-image');
    });
  };

  const applyStagger = (root = document) => {
    root.querySelectorAll(STAGGER_CONTAINERS).forEach((container) => {
      const children = Array.from(container.children);

      children.forEach((child, index) => {
        if (!child.hasAttribute('data-tymzo-reveal')) {
          child.setAttribute('data-tymzo-reveal', 'scale');
        }

        const delay = Math.min(index * 65, 325);
        child.style.setProperty('--tymzo-reveal-delay', `${delay}ms`);
      });
    });
  };

  const revealWithoutAnimation = (root = document) => {
    root.querySelectorAll(REVEAL_SELECTOR).forEach((element) => {
      element.classList.add('is-visible');
    });
  };

  const observeElements = (root = document) => {
    const elements = Array.from(
      root.querySelectorAll(`${REVEAL_SELECTOR}:not(.is-visible)`)
    );

    if (!elements.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      elements.forEach((element) => {
        element.classList.add('is-visible');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.08,
        rootMargin: '0px 0px -7% 0px'
      }
    );

    elements.forEach((element) => observer.observe(element));
  };

  const init = (root = document) => {
    injectStyles();
    markElements(root);
    applyStagger(root);

    if (reduceMotion) {
      revealWithoutAnimation(root);
    } else {
      observeElements(root);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => init(),
      { once: true }
    );
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    event.target
      ?.closest?.('[data-tymzo-reveal]')
      ?.classList.add('is-visible');
  });
})();